import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import type { BaseTexture } from "@babylonjs/core/Materials/Textures/baseTexture";
import type { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Matrix, Quaternion, Vector3 } from "@babylonjs/core/Maths/math.vector";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import type { Scene } from "@babylonjs/core/scene";
import "@babylonjs/core/Meshes/thinInstanceMesh";
import { ctx2d, makeDynamicTexture } from "../../../scene/core/materials";
import type { Ctx2D } from "./paint";

/**
 * Everything the casino's 3D creates goes into one bag, so leaving the room disposes all of it (meshes, materials,
 * textures, nodes), in reverse order of creation.
 */
export class Bag {
  private items: Array<{ dispose(): void }> = [];
  add<T extends { dispose(): void }>(x: T): T {
    this.items.push(x);
    return x;
  }
  dispose(): void {
    for (const x of this.items.reverse()) {
      try {
        x.dispose();
      } catch {
        // already gone with its parent
      }
    }
    this.items = [];
  }
  get size(): number {
    return this.items.length;
  }
}

/** Lit vertex colours (the room's furniture). */
export function litVertex(scene: Scene, name: string): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseColor = Color3.White();
  m.specularColor = new Color3(0.08, 0.08, 0.08);
  m.specularPower = 32;
  m.ambientColor = Color3.White();
  return m;
}

/** Unlit vertex colours: lamps, neon, bulbs (they glow in any light). */
export function unlitVertex(scene: Scene, name: string): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.disableLighting = true;
  m.diffuseColor = Color3.Black();
  m.emissiveColor = Color3.White();
  m.ambientColor = Color3.Black();
  m.specularColor = Color3.Black();
  return m;
}

/** Unlit texture (cards, reels, the wheel face, signs): always readable, two-sided. */
export function unlitTexture(scene: Scene, name: string, tex: BaseTexture): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.disableLighting = true;
  m.diffuseColor = Color3.Black();
  m.emissiveColor = Color3.Black();
  m.emissiveTexture = tex;
  m.specularColor = Color3.Black();
  m.backFaceCulling = false;
  return m;
}

/** Lit texture with a little self-light (felt, carpets): two-sided. */
export function litTexture(scene: Scene, name: string, tex: BaseTexture, glow = 0.15): StandardMaterial {
  const m = new StandardMaterial(name, scene);
  m.diffuseTexture = tex;
  m.specularColor = new Color3(0.03, 0.03, 0.03);
  m.emissiveColor = new Color3(glow, glow, glow);
  m.ambientColor = Color3.White();
  m.backFaceCulling = false;
  return m;
}

/** A canvas texture painted once. */
export function paintedTexture(bag: Bag, scene: Scene, name: string, w: number, h: number, paint: (ctx: Ctx2D) => void, opts: { wrap?: boolean; alpha?: boolean } = {}): DynamicTexture {
  const tex = bag.add(makeDynamicTexture(scene, name, w, h, opts));
  paint(ctx2d(tex) as unknown as Ctx2D);
  tex.update();
  return tex;
}

/** Thin instances of one part (chips, rings, bulbs) with a colour per instance; refilled whenever the scene changes. */
export class InstancedParts {
  private readonly matrices: Float32Array;
  private readonly colors: Float32Array;
  private n = 0;
  private readonly m = new Matrix();
  private readonly q = new Quaternion();
  private readonly s = new Vector3();
  private readonly t = new Vector3();

  constructor(
    readonly mesh: Mesh,
    readonly capacity: number,
  ) {
    this.matrices = new Float32Array(capacity * 16);
    this.colors = new Float32Array(capacity * 4);
    mesh.thinInstanceSetBuffer("matrix", this.matrices, 16, false);
    mesh.thinInstanceSetBuffer("color", this.colors, 4, false);
    mesh.thinInstanceCount = 0;
    mesh.isPickable = false;
  }

  get count(): number {
    return this.n;
  }

  begin(): void {
    this.n = 0;
  }

  push(x: number, y: number, z: number, color: Color3, scale = 1, yaw = 0, alpha = 1): void {
    if (this.n >= this.capacity) return;
    Quaternion.RotationYawPitchRollToRef(yaw, 0, 0, this.q);
    this.s.set(scale, scale, scale);
    this.t.set(x, y, z);
    Matrix.ComposeToRef(this.s, this.q, this.t, this.m);
    this.m.copyToArray(this.matrices, this.n * 16);
    this.colors[this.n * 4] = color.r;
    this.colors[this.n * 4 + 1] = color.g;
    this.colors[this.n * 4 + 2] = color.b;
    this.colors[this.n * 4 + 3] = alpha;
    this.n += 1;
  }

  /** Recolour instance i in place (blinking bulbs). */
  setColor(i: number, color: Color3): void {
    if (i >= this.n) return;
    this.colors[i * 4] = color.r;
    this.colors[i * 4 + 1] = color.g;
    this.colors[i * 4 + 2] = color.b;
  }

  commit(colorsOnly = false): void {
    if (!colorsOnly) this.mesh.thinInstanceBufferUpdated("matrix");
    this.mesh.thinInstanceBufferUpdated("color");
    this.mesh.thinInstanceCount = this.n;
    if (!colorsOnly) this.mesh.thinInstanceRefreshBoundingInfo(false);
    this.mesh.setEnabled(this.n > 0);
  }
}

export const hex = (h: string) => Color3.FromHexString(h);
